import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import Button from '../../components/Button';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface WeekdayRow {
  dayOfWeek: number;
  timeIn: string;
  timeOut: string;
  cutoffTime: string;
  lunchBreakMinutes: number;
  nrmHours: string;
  isRestDay: boolean;
  isHalfDay: boolean;
  showOffInsteadOfRest: boolean;
  otRate: string;
  otMaxHours: string;
  otExtraRate: string;
  phOtRate: string;
  phOtMaxHours: string;
  phOtExtraRate: string;
  monitorBreak: boolean;
}

const emptyRow = (day: number): WeekdayRow => ({
  dayOfWeek: day,
  timeIn: '',
  timeOut: '',
  cutoffTime: '',
  lunchBreakMinutes: 0,
  nrmHours: '',
  isRestDay: day === 0,
  isHalfDay: false,
  showOffInsteadOfRest: false,
  otRate: '',
  otMaxHours: '',
  otExtraRate: '',
  phOtRate: '',
  phOtMaxHours: '',
  phOtExtraRate: '',
  monitorBreak: false,
});

export default function ShiftWeekdays() {
  const { id } = useParams<{ id: string }>();
  const [rows, setRows] = useState<WeekdayRow[]>(Array.from({ length: 7 }, (_, i) => emptyRow(i)));
  const [shiftName, setShiftName] = useState('');
  const [excludeDays, setExcludeDays] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [shiftRes, weekdaysRes] = await Promise.all([api.get(`/tms/shifts/${id}`), api.get(`/tms/shifts/${id}/weekdays`)]);
    setShiftName(shiftRes.data.shift?.name || '');
    const existing = weekdaysRes.data.weekdays || [];
    setRows(
      Array.from({ length: 7 }, (_, day) => {
        const found = existing.find((w: any) => w.day_of_week === day);
        if (!found) return emptyRow(day);
        return {
          dayOfWeek: day,
          timeIn: found.time_in || '',
          timeOut: found.time_out || '',
          cutoffTime: found.cutoff_time || '',
          lunchBreakMinutes: found.lunch_break_minutes || 0,
          nrmHours: found.nrm_hours ?? '',
          isRestDay: found.is_rest_day || false,
          isHalfDay: found.is_half_day || false,
          showOffInsteadOfRest: found.show_off_instead_of_rest || false,
          otRate: found.ot_rate ?? '',
          otMaxHours: found.ot_max_hours ?? '',
          otExtraRate: found.ot_extra_rate ?? '',
          phOtRate: found.ph_ot_rate ?? '',
          phOtMaxHours: found.ph_ot_max_hours ?? '',
          phOtExtraRate: found.ph_ot_extra_rate ?? '',
          monitorBreak: found.monitor_break || false,
        };
      })
    );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateRow = (day: number, patch: Partial<WeekdayRow>) => {
    setRows((prev) => prev.map((r) => (r.dayOfWeek === day ? { ...r, ...patch } : r)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/tms/shifts/${id}/weekdays`, { weekdays: rows });
      alert('Saved');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const copySundayToAll = async () => {
    setSaving(true);
    try {
      await api.post(`/tms/shifts/${id}/weekdays/copy`, { excludeDays });
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to copy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Shift Weekday Grid — {shiftName}</h1>
        <Link to="/tms/shifts" className="text-blue-600 hover:underline text-sm">
          &larr; Back to Shifts
        </Link>
      </div>

      <div className="mb-4 flex items-center gap-3 text-sm">
        <span>Batch-copy Sunday row to all days, excluding:</span>
        {DAYS.slice(1).map((label, idx) => {
          const day = idx + 1;
          return (
            <label key={day} className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={excludeDays.includes(day)}
                onChange={(e) =>
                  setExcludeDays((prev) => (e.target.checked ? [...prev, day] : prev.filter((d) => d !== day)))
                }
              />
              {label}
            </label>
          );
        })}
        <Button variant="secondary" onClick={copySundayToAll} disabled={saving}>
          Copy Sunday &rarr; All
        </Button>
      </div>

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-2 text-left">Day</th>
              <th className="p-2 text-left">Time In</th>
              <th className="p-2 text-left">Time Out</th>
              <th className="p-2 text-left">Cutoff</th>
              <th className="p-2 text-left">Lunch (min)</th>
              <th className="p-2 text-left">NRM Hrs</th>
              <th className="p-2 text-left">Rest Day</th>
              <th className="p-2 text-left">Half Day</th>
              <th className="p-2 text-left">Show OFF</th>
              <th className="p-2 text-left">OT Rate</th>
              <th className="p-2 text-left">OT Max Hrs</th>
              <th className="p-2 text-left">OT Extra Rate</th>
              <th className="p-2 text-left">PH OT Rate</th>
              <th className="p-2 text-left">PH Max Hrs</th>
              <th className="p-2 text-left">PH Extra Rate</th>
              <th className="p-2 text-left">Monitor Break</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.dayOfWeek} className="border-t">
                <td className="p-2 font-medium">{DAYS[row.dayOfWeek]}</td>
                <td className="p-1"><input type="time" className="border rounded px-1 py-0.5 w-24" value={row.timeIn} onChange={(e) => updateRow(row.dayOfWeek, { timeIn: e.target.value })} /></td>
                <td className="p-1"><input type="time" className="border rounded px-1 py-0.5 w-24" value={row.timeOut} onChange={(e) => updateRow(row.dayOfWeek, { timeOut: e.target.value })} /></td>
                <td className="p-1"><input type="time" className="border rounded px-1 py-0.5 w-24" value={row.cutoffTime} onChange={(e) => updateRow(row.dayOfWeek, { cutoffTime: e.target.value })} /></td>
                <td className="p-1"><input type="number" className="border rounded px-1 py-0.5 w-16" value={row.lunchBreakMinutes} onChange={(e) => updateRow(row.dayOfWeek, { lunchBreakMinutes: Number(e.target.value) })} /></td>
                <td className="p-1"><input type="number" className="border rounded px-1 py-0.5 w-16" value={row.nrmHours} onChange={(e) => updateRow(row.dayOfWeek, { nrmHours: e.target.value })} /></td>
                <td className="p-1 text-center"><input type="checkbox" checked={row.isRestDay} onChange={(e) => updateRow(row.dayOfWeek, { isRestDay: e.target.checked })} /></td>
                <td className="p-1 text-center"><input type="checkbox" checked={row.isHalfDay} onChange={(e) => updateRow(row.dayOfWeek, { isHalfDay: e.target.checked })} /></td>
                <td className="p-1 text-center"><input type="checkbox" checked={row.showOffInsteadOfRest} onChange={(e) => updateRow(row.dayOfWeek, { showOffInsteadOfRest: e.target.checked })} /></td>
                <td className="p-1"><input type="number" step="0.1" className="border rounded px-1 py-0.5 w-16" value={row.otRate} onChange={(e) => updateRow(row.dayOfWeek, { otRate: e.target.value })} /></td>
                <td className="p-1"><input type="number" step="0.1" className="border rounded px-1 py-0.5 w-16" value={row.otMaxHours} onChange={(e) => updateRow(row.dayOfWeek, { otMaxHours: e.target.value })} /></td>
                <td className="p-1"><input type="number" step="0.1" className="border rounded px-1 py-0.5 w-16" value={row.otExtraRate} onChange={(e) => updateRow(row.dayOfWeek, { otExtraRate: e.target.value })} /></td>
                <td className="p-1"><input type="number" step="0.1" className="border rounded px-1 py-0.5 w-16" value={row.phOtRate} onChange={(e) => updateRow(row.dayOfWeek, { phOtRate: e.target.value })} /></td>
                <td className="p-1"><input type="number" step="0.1" className="border rounded px-1 py-0.5 w-16" value={row.phOtMaxHours} onChange={(e) => updateRow(row.dayOfWeek, { phOtMaxHours: e.target.value })} /></td>
                <td className="p-1"><input type="number" step="0.1" className="border rounded px-1 py-0.5 w-16" value={row.phOtExtraRate} onChange={(e) => updateRow(row.dayOfWeek, { phOtExtraRate: e.target.value })} /></td>
                <td className="p-1 text-center"><input type="checkbox" checked={row.monitorBreak} onChange={(e) => updateRow(row.dayOfWeek, { monitorBreak: e.target.checked })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <Button onClick={save} loading={saving}>
          Save Grid
        </Button>
      </div>
    </div>
  );
}
